import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Page } from '@/app/Page';
import { PassTemplateSelector } from '@/features/pass-types';
import type { Pass, PassType, ClientDetailPass } from '@/types';
import { type ClientProfileLocationState } from '@/navigation/paths';
import {
  createPassDraftFromTemplate,
  passDraftToCreateRequest,
  passDraftToUpdateRequest,
  PassModal,
  useCreatePass,
  useDeletePass,
  useUpdatePass,
} from '@/features/passes';
import { ClientEditModal, clientsQueryOptions, useUpdateClient } from '@/features/clients';
import { QueryBoundary, QueryErrorPlaceholder } from '@/shared/ui';
import { ClientProfileHeader } from './client-profile-page/ClientProfileHeader';
import { ClientHistorySection } from './client-profile-page/ClientHistorySection';
import { PassesBlock } from './client-profile-page/PassesBlock';
import styles from './ClientProfilePage.module.css';

export const ClientProfilePage: FC = () => {
  const { clientId } = useParams<{ clientId: string }>();

  if (!clientId) {
    return (
      <Page back={true}>
        <QueryErrorPlaceholder header="Not found" />
      </Page>
    );
  }

  return (
    <Page back={true}>
      <QueryBoundary resetKeys={[clientId]}>
        <ClientProfilePageContent clientId={clientId} />
      </QueryBoundary>
    </Page>
  );
};

interface ClientProfilePageContentProps {
  clientId: string;
}

const ClientProfilePageContent: FC<ClientProfilePageContentProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: client } = useSuspenseQuery(clientsQueryOptions.detail(clientId));
  const [isClientEditModalOpen, setIsClientEditModalOpen] = useState(false);

  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingPass, setEditingPass] = useState<ClientDetailPass | undefined>();

  const updateClientMutation = useUpdateClient({
    onSuccess: () => {
      setIsClientEditModalOpen(false);
    },
  });

  const createPassMutation = useCreatePass({
    onSuccess: () => {
      setIsAddModalOpen(false);
      setEditingPass(undefined);
    },
  });

  const updatePassMutation = useUpdatePass({
    onSuccess: () => {
      setIsEditModalOpen(false);
      setEditingPass(undefined);
    },
  });

  const deletePassMutation = useDeletePass();

  const handleStartEditClient = (): void => {
    setIsClientEditModalOpen(true);
  };

  const handleSaveClient = async (clientData: { name: string }): Promise<void> => {
    await updateClientMutation.mutateAsync({
      id: client.id,
      data: { name: clientData.name },
    });
  };

  const handlePassClick = (pass: ClientDetailPass): void => {
    setEditingPass(pass);
    setIsEditModalOpen(true);
  };

  const handleTemplateSelect = (template: PassType): void => {
    setIsTemplateSelectorOpen(false);

    const draft = createPassDraftFromTemplate(template, { id: template.id });
    setEditingPass(draft);
    setIsAddModalOpen(true);
  };

  const openTemplateSelector = (): void => {
    setIsTemplateSelectorOpen(true);
  };

  useEffect(() => {
    const state = (location.state as ClientProfileLocationState | null) ?? null;
    if (!state) return;

    if (state.createdTemplate) {
      const draft = createPassDraftFromTemplate(state.createdTemplate, {
        id: state.createdTemplate.id,
      });
      setIsTemplateSelectorOpen(false);
      setEditingPass(draft);
      setIsAddModalOpen(true);
      void navigate('.', { replace: true, state: {} });
      return;
    }

    if (state.openAddPass) {
      setIsTemplateSelectorOpen(true);
      void navigate('.', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const handleCreatePass = async (passData: Omit<Pass, 'id'>): Promise<void> => {
    await createPassMutation.mutateAsync({
      clientId: client.id,
      data: passDraftToCreateRequest(passData),
    });
  };

  const handleEditPass = async (passData: Omit<Pass, 'id'>): Promise<void> => {
    if (!editingPass) return;

    await updatePassMutation.mutateAsync({
      id: editingPass.id,
      data: passDraftToUpdateRequest(passData),
      clientId: client.id,
    });
  };

  const handleDeletePass = async (id: string): Promise<void> => {
    await deletePassMutation.mutateAsync({ id, clientId: client.id });
  };

  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
    setEditingPass(undefined);
  };

  return (
    <div className={styles.container}>
      <ClientProfileHeader client={client} onRename={handleStartEditClient} />

      <PassesBlock
        clientId={client.id}
        passes={client.passes}
        onPassClick={handlePassClick}
        onAddPass={openTemplateSelector}
      />

      <ClientHistorySection clientId={clientId} />

      <PassTemplateSelector
        isOpen={isTemplateSelectorOpen}
        onClose={() => setIsTemplateSelectorOpen(false)}
        onSelect={(template) => void handleTemplateSelect(template)}
        returnTo={location.pathname}
      />

      <PassModal
        isOpen={isAddModalOpen}
        mode="add"
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingPass(undefined);
        }}
        onSave={handleCreatePass}
        draftKey={`add-pass:${client.id}`}
        isLoading={createPassMutation.isPending}
        editPass={editingPass}
      />

      <PassModal
        isOpen={isEditModalOpen}
        mode="edit"
        onClose={handleCloseEditModal}
        onSave={handleEditPass}
        onDelete={handleDeletePass}
        editPass={editingPass}
        isLoading={updatePassMutation.isPending || deletePassMutation.isPending}
      />

      <ClientEditModal
        isOpen={isClientEditModalOpen}
        onClose={() => setIsClientEditModalOpen(false)}
        onSave={handleSaveClient}
        client={client}
        isLoading={updateClientMutation.isPending}
      />
    </div>
  );
};
